import {
  Environment,
  Locale,
  getHtmlLangByLocale,
  getUsercentricsId,
  getVersionName,
} from '@aboutyou/config';
import {generateTrackingHtml} from '@aboutyou/tracking';

import {
  DD_BROWSER_LOGGERS,
  DD_ENV_NAME_MAP,
  FALLBACK_LOGS_MIDDLEWARE_ROUTE,
  FALL_LOGS_TOKEN,
  LOGS_TOKEN_HEADER,
} from '@aboutyou/mlm';
import {
  CONSENT_VIEW_INITIALISED,
  SERVICE_CONSENT_SEARCH_PARAM,
} from '@aboutyou/storage';
import {stripIndent} from 'common-tags';
import React from 'react';
import {renderToStaticMarkup} from 'react-dom/server';
import {datadogBrowserLogsScript} from './datadog-browser-logs-script';
import {handleMissingBody} from './handleMissingBody';
import {Head} from './head';
import {HeadContext} from './head-elements-context';
import {loadConsentsScript} from './load-consents';
import {shopInitScript} from './shopInitScript';
import {
  usercentricsScriptPreviousVersion,
  usercentrictsscript,
} from './user-centrics-script';

export const usercentricsScriptId = 'usercentrics-cmp';

interface Params {
  locale: Locale;
  disableWebTracking: boolean;
  disableDatadogBrowser: boolean;
  isUsercentricsEnabled: boolean;
  disableManifestFile: boolean;
  additionalHTML: string;
  hostname: string;
  environment: Environment;
  assetsUrl: string | undefined;
  device: 'mobile' | 'desktop';
  viewName?: string;
  currency?: string;
  shopId?: number;
  isBotMode: boolean;
  isAppCheckout: boolean;
}

export const getHtmlHeadTemplate = (
  headContext: HeadContext,
  params: Params,
) => {
  const {
    locale,
    disableWebTracking,
    disableDatadogBrowser,
    additionalHTML = '',
    hostname,
    environment,
    disableManifestFile,
    assetsUrl,
    device,
    viewName,
    isUsercentricsEnabled,
    shopId,
    currency,
    isBotMode,
    isAppCheckout,
  } = params;

  const htmlLang = getHtmlLangByLocale(locale);

  const {headElements: headTrackingElements} = generateTrackingHtml(hostname);

  const additionalElements = [];
  if (!disableWebTracking) {
    additionalElements.push(...headTrackingElements);
  }

  if (!isAppCheckout) {
    additionalElements.push(
      <script
        key={'shop-init'}
        data-testid="shop-init"
        type="text/javascript"
        charSet="UTF-8"
        dangerouslySetInnerHTML={{
          __html: `(${shopInitScript.toString()})({shopId: "${shopId}", locale: "${locale}", currency: "${currency}", device: "${device}"})`,
        }}
      />,
    );
  }

  additionalElements.push(<meta key="meta-ssr" name="ssr" />);

  const version = getVersionName();

  if (!disableDatadogBrowser && process.env.DATADOG_BROWSER_LOGS_CLIENT_TOKEN) {
    // Adds the Datadog Browser Logs SDK
    additionalElements.push(
      <script
        key="datadog-browser-logs-script"
        id="datadog-browser-logs-script"
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: datadogBrowserLogsScript({
            environment,
            version,
            viewName,
            device,
            isUsercentricsEnabled,
            isBotMode,
            locale,
            shopId: shopId!,
            clientToken: process.env.DATADOG_BROWSER_LOGS_CLIENT_TOKEN,
          }),
        }}
      />,
    );
  }

  const env = DD_ENV_NAME_MAP[environment];

  additionalElements.push(
    <script
      key="handle-missing-body"
      data-testid="handle-missing-body"
      type="text/javascript"
      charSet="UTF-8"
      dangerouslySetInnerHTML={{
        __html: `(${handleMissingBody.toString()})({
          env: "${env}",
          locale: "${locale}",
          shop_id: ${shopId},
          version: "${version}",
          device: "${device}",
          hostname: "${hostname}",
          fallbackLogsMiddlewareRoute: "${FALLBACK_LOGS_MIDDLEWARE_ROUTE}",
          loggerLevel: "${DD_BROWSER_LOGGERS.UNSAMPLED_LOGS}",
          logsToken: "${FALL_LOGS_TOKEN}",
          logsTokenHeader: "${LOGS_TOKEN_HEADER}",
        })`,
      }}
    />,
  );

  // consent-banner is not used in app-checkout
  if (!isAppCheckout) {
    additionalElements.push(
      <script
        key="consent-sync"
        data-testid="consent-sync"
        type="text/javascript"
        charSet="UTF-8"
        dangerouslySetInnerHTML={{
          __html: `(${loadConsentsScript.toString()})({SERVICE_CONSENT_EVENT: "${CONSENT_VIEW_INITIALISED}", searchParamName: "${SERVICE_CONSENT_SEARCH_PARAM}"})`,
        }}
      />,
    );
  }

  if (isUsercentricsEnabled) {
    additionalElements.push(
      <React.Fragment key="usercentrics">
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (${usercentrictsscript.toString()})();
          `,
          }}
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (${usercentricsScriptPreviousVersion.toString()})();
          `,
          }}
        />
        <link
          rel="preload"
          href="https://t2.aboutyou.com/tapi/basic-js/lib.js?t=AYC"
          as="script"
        />

        <script
          src="https://t2.aboutyou.com/tapi/basic-js/lib.js?t=AYC"
          async
        ></script>

        <link
          rel="preload"
          href="https://web.cmp.usercentrics.eu/ui/loader.js"
          as="script"
        />
        <script
          id={usercentricsScriptId}
          src="https://web.cmp.usercentrics.eu/ui/loader.js"
          data-settings-id={getUsercentricsId(locale)}
          data-version={environment}
          async
          type="text/javascript"
          charSet="UTF-8"
          {...(environment !== 'production' && {
            'data-draft': 'true',
          })}
        />
      </React.Fragment>,
    );
  }

  const headElements = renderToStaticMarkup(
    <Head
      additionalElements={additionalElements}
      headContext={headContext}
      disableManifestFile={disableManifestFile}
    />,
  );

  const publicPathScript = assetsUrl
    ? `<script>window.__PUBLIC_ASSETS_URL__ = '${assetsUrl}'</script>`
    : '';

  return stripIndent`
    <!doctype html>
    <html lang="${htmlLang}" dir="ltr">
      ${headElements.replace(
        '</head>',
        `${publicPathScript}${additionalHTML}</head>`,
      )}
      <body>
          <main data-testid="react-root" id="react-root">`;
};
